import { AsyncFeedCurrency, AsyncInitCurrency, Currency, CurrencyNetworkEnum, FiatCurrency } from '../api/api';
import { cfgBaseCurrency, cfgBaseCurrencySymbol } from '@infinitro/config';
import { writable } from 'svelte/store';
const CURRENCY_PRICE_FEED_INTERVAL = 5000;
export var RoundDirection;
(function (RoundDirection) {
    RoundDirection["up"] = "up";
    RoundDirection["down"] = "down";
    RoundDirection["nearest"] = "nearest";
})(RoundDirection || (RoundDirection = {}));
export const baseCurrencySymbol = cfgBaseCurrencySymbol();
export const baseCurrency = cfgBaseCurrency();
export const defaultDecimalDisplayLength = 2;
export const staticCurrencyInfo = [
    {
        currency: Currency.usd,
        display: 2,
        fiatSymbol: '$',
        name: 'USD'
    },
    {
        display: 7,
        name: 'BTC',
        currency: Currency.btc
    },
    {
        display: 6,
        name: 'ETH',
        currency: Currency.eth
    },
    {
        display: 2,
        name: 'USDT',
        currency: Currency.usdt
    }
];
export const baseCurrencyDisplayLength = staticCurrencyInfo.find((c) => c.currency === baseCurrency)?.display ?? 2;
let _stateCurrencyInfo = staticCurrencyInfo;
export const stateCurrencyInfo = writable(staticCurrencyInfo);
stateCurrencyInfo.subscribe((info) => {
    _stateCurrencyInfo = info;
});
export const currencySymbol = (currency) => {
    return _stateCurrencyInfo.find((token) => token.currency == currency)?.fiatSymbol || '';
};
export const decimalDisplayLength = (currency) => {
    return (_stateCurrencyInfo.find((token) => token.currency == currency)?.display ||
        defaultDecimalDisplayLength);
};
// displays the string amount with the correct number of decimal places for the given currency
export const formatStringAmountCurrency = (amount, currency) => {
    if (!amount)
        return '';
    const amountParts = amount.split('.');
    const digits = decimalDisplayLength(currency);
    if (amountParts.length === 1) {
        return amount + '.' + '0'.repeat(digits);
    }
    if (amountParts[1].length >= digits) {
        return amountParts[0] + '.' + amountParts[1].substring(0, digits);
    }
    return amountParts[0] + '.' + amountParts[1] + '0'.repeat(digits - amountParts[1].length);
};
// formats an amount of the given currency to its correct display precision
export const formatCurrencyString = (amount, currency) => {
    const digits = decimalDisplayLength(currency);
    return amount.toFixed(digits);
};
export const formatBaseCurrencyNumber = (amount, withDecimals = true) => baseCurrencyDisplayLength > 0 && (withDecimals || amount != Math.floor(amount))
    ? Math.floor(amount * Math.pow(10, baseCurrencyDisplayLength)) /
        Math.pow(10, baseCurrencyDisplayLength)
    : Math.floor(amount);
export const formatBaseCurrencyNumberString = (amount, withDecimals = true) => `${formatBaseCurrencyNumber(amount, withDecimals).toFixed(withDecimals || amount != Math.floor(amount) ? baseCurrencyDisplayLength : 0)}`;
export const formatBaseCurrencyString = (amount, withDecimals = true) => `${baseCurrencySymbol}${formatBaseCurrencyNumber(amount, withDecimals)}`;
export const formatBaseWithCurrency = (amount, withDecimals = true) => `${formatBaseCurrencyNumber(amount, withDecimals)} ${baseCurrency.toUpperCase()}`;
export const currencyPrices = writable([]);
export const currencyInfo = writable([]);
export const isFiat = (currency) => {
    if (!currency)
        return false;
    return Object.keys(FiatCurrency).includes(currency?.toUpperCase());
};
export const isCrypto = (currency) => {
    if (!currency)
        return false;
    return (Object.keys(Currency).includes(currency?.toLowerCase()) &&
        !Object.keys(FiatCurrency).includes(currency?.toUpperCase()));
};
export const isFiatNetwork = (network) => {
    if (!network)
        return false;
    return [
        CurrencyNetworkEnum.cards,
        CurrencyNetworkEnum.fiat,
        CurrencyNetworkEnum.cash,
        CurrencyNetworkEnum.interac,
        CurrencyNetworkEnum.manual,
        CurrencyNetworkEnum.other
    ].includes(network);
};
export const isCryptoNetwork = (network) => {
    if (!network)
        return false;
    return ![
        CurrencyNetworkEnum.cards,
        CurrencyNetworkEnum.fiat,
        CurrencyNetworkEnum.cash,
        CurrencyNetworkEnum.interac,
        CurrencyNetworkEnum.manual,
        CurrencyNetworkEnum.other
    ].includes(network);
};
export const getCurrencyDisplayDecimals = (currency) => {
    return _stateCurrencyInfo.find((token) => token.currency == currency)?.display || 2;
};
/** Keeps partial numeric input (e.g. "1.") while capping fractional digits. */
export const limitNumericInputDecimals = (value, maxDecimals) => {
    if (!value)
        return value;
    const cleaned = value.replace(/[^0-9.]/g, '');
    const dotIndex = cleaned.indexOf('.');
    if (dotIndex === -1)
        return cleaned;
    const beforeDot = cleaned.slice(0, dotIndex);
    let afterDot = cleaned.slice(dotIndex + 1).replace(/\./g, '');
    if (maxDecimals === 0)
        return beforeDot || '0';
    if (afterDot.length > maxDecimals) {
        afterDot = afterDot.slice(0, maxDecimals);
    }
    return `${beforeDot}.${afterDot}`;
};
let _cryptoLoading = false;
export const cryptoError = writable(false);
export async function fetchAndSetCryptoPrices(retries = 3, delay = 1000) {
    if (_cryptoLoading) {
        console.log('Already fetching crypto prices.');
        return;
    }
    cryptoError.set(false);
    try {
        await fetchWithRetry(retries, delay);
    }
    catch (error) {
        console.log('Error fetching crypto prices', JSON.stringify(error, null, 2));
        cryptoError.set(true);
    }
    finally {
        _cryptoLoading = false;
    }
}
let _initalizedCurrencies = false;
async function fetchWithRetry(retries, delay) {
    try {
        if (_initalizedCurrencies) {
            const response = await AsyncFeedCurrency({});
            currencyPrices.set(response?.data?.currencyPrices);
            return response;
        }
        else {
            const response = await AsyncInitCurrency({});
            const currencies = response?.data?.listCurrencies;
            currencyInfo.set(currencies);
            stateCurrencyInfo.set(currencies);
            currencyPrices.set(response?.data?.currencyPrices);
            _initalizedCurrencies = true;
            return response;
        }
    }
    catch (error) {
        if (retries === 0) {
            throw error;
        }
        console.log(`Retrying fetching crypto prices in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return fetchWithRetry(retries - 1, delay);
    }
}
let timer;
export const startCurrencyPriceFeed = () => {
    if (!timer) {
        timer = setInterval(() => {
            fetchAndSetCryptoPrices();
        }, CURRENCY_PRICE_FEED_INTERVAL);
    }
};
